"use client";

import { useTranslations } from "next-intl";
import { useEffect } from "react";

function readField(formData: FormData, name: string) {
  const value = formData.get(name);
  return typeof value === "string" ? value : "";
}

function buildPhone(formData: FormData) {
  const dialCode = readField(formData, "phoneCountryCode").trim();
  const phone = readField(formData, "phone").trim();

  if (!phone) {
    return "";
  }

  if (!dialCode || phone.startsWith("+")) {
    return phone;
  }

  return `${dialCode} ${phone}`;
}

export default function MintLeadFormBridge() {
  const t = useTranslations("mindlabMint.leadForm");

  useEffect(() => {
    const form = document.getElementById("leadForm");
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const status = document.getElementById("formStatus");
    const submitButton = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]',
    );

    const setStatus = (message: string, state?: "success" | "error") => {
      if (!status) {
        return;
      }

      status.textContent = message;
      if (state) {
        status.dataset.state = state;
      } else {
        delete status.dataset.state;
      }
    };

    const handleSubmit = async (event: SubmitEvent) => {
      event.preventDefault();

      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const formData = new FormData(form);
      submitButton?.setAttribute("disabled", "true");
      if (submitButton) {
        submitButton.textContent = t("submitting");
      }
      setStatus(t("sending"));

      try {
        const response = await fetch("/api/mindlab/mint/leads", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            institute: readField(formData, "institute"),
            fullName: readField(formData, "fullName"),
            position: readField(formData, "position"),
            email: readField(formData, "email"),
            phone: buildPhone(formData),
            models: readField(formData, "models"),
            applicationReasons: readField(formData, "applicationReasons"),
            page: window.location.href,
            language: document.documentElement.lang,
          }),
        });

        if (!response.ok) {
          throw new Error("Lead submit failed");
        }

        form.reset();
        setStatus(t("success"), "success");
      } catch {
        setStatus(t("error"), "error");
      } finally {
        submitButton?.removeAttribute("disabled");
        if (submitButton) {
          submitButton.textContent = t("submit");
        }
      }
    };

    form.addEventListener("submit", handleSubmit);

    return () => {
      form.removeEventListener("submit", handleSubmit);
    };
  }, [t]);

  return null;
}
